import { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { invoicesService } from '@/services/invoices-service'

function fmt(val: number): string {
  return '$' + val.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function fmtDate(str?: string | null): string {
  if (!str) return '—'
  return new Date(str).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

function titleCase(str: string): string {
  return str
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

type PaymentOption = 'credit_card' | 'ach' | null

export function PublicInvoicePage() {
  const { shareableLink } = useParams<{ shareableLink: string }>()
  const [invoice, setInvoice] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selectedPayment, setSelectedPayment] = useState<PaymentOption>(null)

  useEffect(() => {
    if (!shareableLink) {
      setError('Invalid invoice link')
      setLoading(false)
      return
    }

    async function load() {
      try {
        const data = await invoicesService.getPublic(shareableLink!)
        setInvoice(data)
      } catch (err) {
        console.error('Failed to load invoice:', err)
        setError('This invoice could not be found or is no longer available.')
      } finally {
        setLoading(false)
      }
    }

    load()
  }, [shareableLink])

  if (loading) {
    return (
      <div className="pub-inv-page">
        <div className="pub-inv-loading">Loading invoice...</div>
      </div>
    )
  }

  if (error || !invoice) {
    return (
      <div className="pub-inv-page">
        <div className="pub-inv-error-card">
          <div className="pub-inv-error-icon">!</div>
          <h2>Invoice Not Found</h2>
          <p>{error || 'This invoice could not be found.'}</p>
        </div>
      </div>
    )
  }

  const b = invoice.brandingSnapshot || invoice.tenant?.branding || {}
  const brandColor = b.primaryColor || '#0074F4'
  const lineItems = Array.isArray(invoice.lineItemsSnapshot) ? invoice.lineItemsSnapshot : []
  const isPaid = invoice.status === 'paid'
  const isVoid = invoice.status === 'void'
  const payments = invoice.payments || []
  const totalPaid = payments.reduce((sum: number, p: any) => sum + (p.amount || 0), 0)

  // Convenience fee
  const convenienceFeePercent = b.convenienceFeePercent || invoice.tenant?.branding?.convenienceFeePercent || 0
  const acceptCreditCard = b.acceptCreditCard !== false
  const acceptACH = b.acceptACH !== false

  // Calculate amounts based on payment method
  const convenienceFee = selectedPayment === 'credit_card' && convenienceFeePercent > 0
    ? invoice.amountDue * (convenienceFeePercent / 100)
    : 0
  const totalWithFee = invoice.amountDue + convenienceFee

  // Overdue check
  const isOverdue = !isPaid && !isVoid && invoice.dueDate && new Date(invoice.dueDate) < new Date()

  return (
    <div className="pub-inv-page" style={{ '--pub-inv-brand': brandColor } as React.CSSProperties}>
      {/* Branded Header Bar */}
      <div className="pub-inv-header">
        <div className="pub-inv-header-inner">
          {b.logoUrl && <img src={b.logoUrl} alt="" className="pub-inv-logo" />}
          <span className="pub-inv-company-name">{b.companyName || 'Invoice'}</span>
        </div>
      </div>

      <div className="pub-inv-content">
        {/* Status Banner */}
        {isPaid && (
          <div className="pub-inv-banner pub-inv-banner-paid">
            <span className="pub-inv-banner-icon">&#10003;</span>
            This invoice has been paid in full
          </div>
        )}
        {isVoid && (
          <div className="pub-inv-banner pub-inv-banner-void">
            This invoice has been voided
          </div>
        )}
        {isOverdue && !isPaid && (
          <div className="pub-inv-banner pub-inv-banner-overdue">
            This invoice is past due
          </div>
        )}

        <div className="pub-inv-grid">
          {/* LEFT: Invoice Document */}
          <div className="pub-inv-doc">
            {/* Invoice Header */}
            <div className="pub-inv-doc-header">
              <div>
                <div className="pub-inv-doc-title">INVOICE</div>
                <div className="pub-inv-doc-number">{invoice.invoiceNumber}</div>
                {invoice.stageLabel && (
                  <div className="pub-inv-doc-stage">{invoice.stageLabel}</div>
                )}
              </div>
              <div className="pub-inv-doc-meta">
                <div className="pub-inv-doc-meta-row">
                  <span className="pub-inv-doc-meta-label">Issued</span>
                  <span>{fmtDate(invoice.issueDate)}</span>
                </div>
                <div className="pub-inv-doc-meta-row">
                  <span className="pub-inv-doc-meta-label">Due</span>
                  <span className={isOverdue ? 'pub-inv-overdue-text' : ''}>{fmtDate(invoice.dueDate)}</span>
                </div>
                <div className="pub-inv-doc-meta-row">
                  <span className="pub-inv-doc-meta-label">Terms</span>
                  <span>{titleCase(invoice.paymentTerms)}</span>
                </div>
              </div>
            </div>

            {/* From / Bill To */}
            <div className="pub-inv-parties">
              <div className="pub-inv-party">
                <div className="pub-inv-party-label">From</div>
                <div className="pub-inv-party-name">{b.companyName || ''}</div>
                {b.companyAddress && <div className="pub-inv-party-detail">{b.companyAddress}</div>}
                {(b.companyCity || b.companyState || b.companyZip) && (
                  <div className="pub-inv-party-detail">
                    {[b.companyCity, b.companyState].filter(Boolean).join(', ')}{b.companyZip ? ` ${b.companyZip}` : ''}
                  </div>
                )}
                {b.companyPhone && <div className="pub-inv-party-detail">{b.companyPhone}</div>}
                {b.companyEmail && <div className="pub-inv-party-detail">{b.companyEmail}</div>}
              </div>
              <div className="pub-inv-party">
                <div className="pub-inv-party-label">Bill To</div>
                <div className="pub-inv-party-name">{invoice.clientName}</div>
                {invoice.clientCompany && <div className="pub-inv-party-detail">{invoice.clientCompany}</div>}
                {invoice.jobAddress && <div className="pub-inv-party-detail">{invoice.jobAddress}</div>}
                {invoice.clientEmail && <div className="pub-inv-party-detail">{invoice.clientEmail}</div>}
                {invoice.clientPhone && <div className="pub-inv-party-detail">{invoice.clientPhone}</div>}
              </div>
            </div>

            {/* Staged Billing Context — shows this invoice is one portion of the project */}
            {invoice.billingSummary && (() => {
              const bs = invoice.billingSummary
              return (
                <div className="pub-inv-billing-sched">
                  <div className="pub-inv-billing-head">
                    Staged Billing — Stage {bs.stageNumber} of {bs.stageCount}
                    {bs.stageLabel ? ` · ${bs.stageLabel}` : ''}
                    {bs.stagePercent ? ` (${bs.stagePercent}% of contract)` : ''}
                  </div>
                  <div className="pub-inv-billing-cells">
                    <div className="pub-inv-billing-cell">
                      <span>Total Project Price</span><strong>{fmt(bs.projectTotal)}</strong>
                    </div>
                    <div className="pub-inv-billing-cell">
                      <span>Previously Billed</span><strong>{fmt(bs.previouslyBilled)}</strong>
                    </div>
                    <div className="pub-inv-billing-cell pub-inv-billing-cell-accent">
                      <span>This Invoice</span><strong>{fmt(bs.thisStageAmountDue)}</strong>
                    </div>
                    <div className="pub-inv-billing-cell">
                      <span>Remaining After This</span><strong>{fmt(bs.remainingBalance)}</strong>
                    </div>
                  </div>
                  <table className="pub-inv-billing-table">
                    <tbody>
                      {bs.stages.map((s: any) => (
                        <tr key={s.stageNumber} className={s.isCurrent ? 'pub-inv-billing-current' : ''}>
                          <td>{s.stageLabel || `Stage ${s.stageNumber}`}{s.isCurrent ? ' (this invoice)' : ''}</td>
                          <td className="pub-inv-billing-num">{s.invoiceNumber || 'Scheduled'}</td>
                          <td className="pub-inv-billing-status">{titleCase(s.status)}</td>
                          <td className="pub-inv-col-right pub-inv-col-amount">{fmt(s.total)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {bs.amountPaidToDate > 0 && (
                    <div className="pub-inv-billing-paid-note">
                      Paid to date across all stages: <strong>{fmt(bs.amountPaidToDate)}</strong>
                    </div>
                  )}
                </div>
              )
            })()}

            {/* Project Title */}
            {invoice.title && (
              <div className="pub-inv-project">
                <span className="pub-inv-project-label">Project:</span> {invoice.title}
              </div>
            )}

            {/* Job Summary */}
            {invoice.notes && (
              <div className="pub-inv-summary">
                <div className="pub-inv-section-label">Job Summary</div>
                <div className="pub-inv-summary-text">{invoice.notes}</div>
              </div>
            )}

            {/* Line Items Table */}
            {lineItems.length > 0 && (
              <div className="pub-inv-items">
                {lineItems[0]?.isSummary ? (
                  /* Category summary format (new invoices) */
                  <table className="pub-inv-table">
                    <thead>
                      <tr>
                        <th>Description</th>
                        <th className="pub-inv-col-right">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {lineItems.map((item: any, idx: number) => (
                        <tr key={idx}>
                          <td>{item.description}</td>
                          <td className="pub-inv-col-right pub-inv-col-amount">
                            {fmt(item.lineTotal || 0)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  /* Granular line items format (legacy invoices) */
                  <table className="pub-inv-table">
                    <thead>
                      <tr>
                        <th>Description</th>
                        <th>Category</th>
                        <th className="pub-inv-col-right">Qty</th>
                        <th className="pub-inv-col-right">Unit Price</th>
                        <th className="pub-inv-col-right">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {lineItems.map((item: any, idx: number) => (
                        <tr key={idx}>
                          <td>{item.description}</td>
                          <td className="pub-inv-cat">{titleCase(item.category || 'other')}</td>
                          <td className="pub-inv-col-right">{item.quantity}</td>
                          <td className="pub-inv-col-right">{fmt(item.rate || 0)}</td>
                          <td className="pub-inv-col-right pub-inv-col-amount">
                            {fmt(item.lineTotal || (item.quantity * item.rate) || 0)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}

            {/* Totals */}
            <div className="pub-inv-totals">
              <div className="pub-inv-totals-row">
                <span>Subtotal</span>
                <span>{fmt(invoice.subtotal)}</span>
              </div>
              {invoice.taxRate > 0 && (
                <div className="pub-inv-totals-row">
                  <span>Tax ({invoice.taxRate}%)</span>
                  <span>{fmt(invoice.taxAmount)}</span>
                </div>
              )}
              {invoice.discountAmount > 0 && (
                <div className="pub-inv-totals-row">
                  <span>Discount</span>
                  <span>-{fmt(invoice.discountAmount)}</span>
                </div>
              )}
              <div className="pub-inv-totals-row pub-inv-totals-total">
                <span>Total</span>
                <span>{fmt(invoice.total)}</span>
              </div>
              {totalPaid > 0 && (
                <div className="pub-inv-totals-row pub-inv-totals-paid">
                  <span>Paid</span>
                  <span>-{fmt(totalPaid)}</span>
                </div>
              )}
            </div>

            {/* Amount Due Banner */}
            {!isPaid && !isVoid && invoice.amountDue > 0 && (
              <div className="pub-inv-amount-due">
                <span>Amount Due</span>
                <span>{fmt(invoice.amountDue)}</span>
              </div>
            )}
            {isPaid && (
              <div className="pub-inv-paid-stamp">PAID</div>
            )}

            {/* Terms & Conditions */}
            {invoice.termsAndConditions && (
              <div className="pub-inv-terms">
                <div className="pub-inv-section-label">Terms &amp; Conditions</div>
                <div className="pub-inv-terms-text">{invoice.termsAndConditions}</div>
              </div>
            )}
          </div>

          {/* RIGHT: Payment Panel */}
          {!isPaid && !isVoid && invoice.amountDue > 0 && (
            <div className="pub-inv-pay-panel">
              <div className="pub-inv-pay-card">
                <div className="pub-inv-pay-header">
                  <div className="pub-inv-pay-label">Amount Due</div>
                  <div className="pub-inv-pay-amount">{fmt(invoice.amountDue)}</div>
                  <div className="pub-inv-pay-due">Due {fmtDate(invoice.dueDate)}</div>
                </div>

                {/* Payment Method Selection */}
                <div className="pub-inv-pay-methods">
                  <div className="pub-inv-pay-methods-label">Select Payment Method</div>

                  {acceptCreditCard && (
                    <button
                      className={`pub-inv-pay-option ${selectedPayment === 'credit_card' ? 'active' : ''}`}
                      onClick={() => setSelectedPayment(selectedPayment === 'credit_card' ? null : 'credit_card')}
                    >
                      <div className="pub-inv-pay-option-header">
                        <span className="pub-inv-pay-option-icon">&#9634;</span>
                        <span className="pub-inv-pay-option-name">Credit / Debit Card</span>
                      </div>
                      {convenienceFeePercent > 0 && (
                        <div className="pub-inv-pay-option-fee">
                          {convenienceFeePercent}% convenience fee applies
                        </div>
                      )}
                    </button>
                  )}

                  {acceptACH && (
                    <button
                      className={`pub-inv-pay-option ${selectedPayment === 'ach' ? 'active' : ''}`}
                      onClick={() => setSelectedPayment(selectedPayment === 'ach' ? null : 'ach')}
                    >
                      <div className="pub-inv-pay-option-header">
                        <span className="pub-inv-pay-option-icon">&#9634;</span>
                        <span className="pub-inv-pay-option-name">ACH / Bank Transfer</span>
                      </div>
                      <div className="pub-inv-pay-option-fee pub-inv-pay-no-fee">No additional fees</div>
                    </button>
                  )}
                </div>

                {/* Convenience Fee Breakdown (when credit card selected) */}
                {selectedPayment === 'credit_card' && convenienceFeePercent > 0 && (
                  <div className="pub-inv-fee-breakdown">
                    <div className="pub-inv-fee-row">
                      <span>Invoice Amount</span>
                      <span>{fmt(invoice.amountDue)}</span>
                    </div>
                    <div className="pub-inv-fee-row">
                      <span>Convenience Fee ({convenienceFeePercent}%)</span>
                      <span>{fmt(convenienceFee)}</span>
                    </div>
                    <div className="pub-inv-fee-row pub-inv-fee-total">
                      <span>Total Charge</span>
                      <span>{fmt(totalWithFee)}</span>
                    </div>
                  </div>
                )}

                {/* Pay Button */}
                {selectedPayment && (
                  <button className="pub-inv-pay-btn" disabled>
                    Pay {fmt(totalWithFee)} — Coming Soon
                  </button>
                )}

                {!selectedPayment && (acceptCreditCard || acceptACH) && (
                  <div className="pub-inv-pay-hint">
                    Select a payment method above to continue
                  </div>
                )}

                {!acceptCreditCard && !acceptACH && (
                  <div className="pub-inv-pay-hint">
                    Please contact {b.companyName || 'us'} directly to arrange payment.
                  </div>
                )}
              </div>

              {/* Contact Info */}
              <div className="pub-inv-contact-card">
                <div className="pub-inv-section-label">Questions?</div>
                <div className="pub-inv-contact-name">{b.companyName || ''}</div>
                {b.companyPhone && <div className="pub-inv-contact-detail">{b.companyPhone}</div>}
                {b.companyEmail && <div className="pub-inv-contact-detail">{b.companyEmail}</div>}
                {b.companyWebsite && (
                  <a href={b.companyWebsite} target="_blank" rel="noopener noreferrer" className="pub-inv-contact-link">
                    {b.companyWebsite.replace(/^https?:\/\//, '')}
                  </a>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="pub-inv-footer">
        Powered by <strong>Paver</strong>
      </div>
    </div>
  )
}
